import { useCallback, useEffect, useId, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import { QuantityInput } from '@/components/ui/QuantityInput'
import { formatDay, todayIso } from '@/lib/format'
import type { AdminFetch } from './AdminCustomers'

type Status = 'AVAILABLE' | 'EXPIRED' | 'IN_USE' | 'USED' | 'REVOKED'
type Coupon = { id: string; code: string; percentOff: number; periods: number; periodsUsed: number; lastDay: string; note: string; createdBy: string; createdAt: string; redeemedAt?: string | null; redeemedBy?: string | null; revokedAt?: string | null; revokedBy?: string | null; status: Status }
type Draft = { percentOff: number; periods: number; lastDay: string; note: string }

const NOTE_LIMIT = 500
const statusLabels: Record<Status, string> = { AVAILABLE: 'Sin canjear', EXPIRED: 'Venció sin canjear', IN_USE: 'En uso', USED: 'Usado', REVOKED: 'Revocado' }

function payments(count: number) {
  return `${count.toLocaleString('es-MX')} ${count === 1 ? 'pago' : 'pagos'}`
}

function inDays(days: number) {
  const day = new Date(`${todayIso()}T12:00:00`)
  day.setDate(day.getDate() + days)
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
}

/**
 * The discount coupons given to a customer: a percentage off its next payments of a plan, mailed to its owner as a
 * code it redeems before a last day. The admin gives them and revokes one so no new payment takes it.
 */
export function AdminCoupons({ companyId, businessName, ownerEmail, adminFetch, describeError }: {
  companyId: string; businessName: string; ownerEmail?: string | null; adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string
}) {
  const id = useId()
  const [coupons, setCoupons] = useState<Coupon[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [giving, setGiving] = useState(false)
  const [giveError, setGiveError] = useState('')
  const [revoking, setRevoking] = useState<Coupon | null>(null)
  const [revokingBusy, setRevokingBusy] = useState(false)
  const [revokeError, setRevokeError] = useState('')
  const [notice, setNotice] = useState('')
  const path = `/admin/companies/${companyId}/coupons`

  const load = useCallback(async () => {
    setLoading(true); setLoadError('')
    try { setCoupons(await adminFetch<Coupon[]>(path)) } catch (cause) { setLoadError(describeError(cause, 'No pudimos leer los cupones.')) } finally { setLoading(false) }
  }, [adminFetch, describeError, path])

  useEffect(() => { void load() }, [load])

  function start() {
    setGiveError(''); setNotice('')
    setDraft({ percentOff: 10, periods: 1, lastDay: inDays(30), note: '' })
  }

  async function give() {
    if (!draft) return
    if (!draft.lastDay || draft.lastDay < todayIso()) { setGiveError('El último día para canjearlo es hoy o uno posterior.'); return }
    if (!draft.note.trim()) { setGiveError('Escribe por qué se da el cupón.'); return }
    setGiving(true); setGiveError('')
    try {
      const updated = await adminFetch<Coupon[]>(path, { method: 'POST', body: { percentOff: draft.percentOff, periods: draft.periods, lastDay: draft.lastDay, note: draft.note.trim() } })
      setCoupons(updated); setDraft(null)
      setNotice(`Cupón ${updated[0]?.code ?? ''} de ${draft.percentOff} % en ${payments(draft.periods)}. Le enviamos el código por correo${ownerEmail ? ` a ${ownerEmail}` : ' al propietario'}.`)
    } catch (cause) { setGiveError(describeError(cause, 'No pudimos dar el cupón.')) } finally { setGiving(false) }
  }

  async function revoke() {
    if (!revoking) return
    setRevokingBusy(true); setRevokeError('')
    try {
      setCoupons(await adminFetch<Coupon[]>(`${path}/${revoking.id}/revoke`, { method: 'POST' }))
      setNotice(`Cupón ${revoking.code} revocado: ningún pago nuevo lo toma.`)
      setRevoking(null)
    } catch (cause) { setRevokeError(describeError(cause, 'No pudimos revocar el cupón.')) } finally { setRevokingBusy(false) }
  }

  return <section className="admin-customer-block" aria-labelledby={`${id}-heading`}>
    <h4 id={`${id}-heading`}>Cupones de descuento</h4>
    <p className="field-hint">Un porcentaje menos en sus siguientes pagos de licencias. El código se envía por correo al propietario, y solo este negocio puede canjearlo.</p>
    {notice && <Notice tone="success">{notice}</Notice>}
    {loading && !coupons ? <LoadingState label="Leyendo los cupones…" />
      : loadError && !coupons ? <ErrorState title={loadError} retry={() => void load()} busy={loading} />
      : <>
        {coupons && coupons.length > 0 && <div className="price-table-wrap"><table>
          <thead><tr><th><span className="sr-only">Acciones</span></th><th>Código</th><th>Descuento</th><th>Pagos usados</th><th>Canjear hasta</th><th>Motivo</th><th>Dio</th><th>Estado</th></tr></thead>
          <tbody>{coupons.map((coupon) => <tr key={coupon.id}>
            <td>{coupon.status === 'AVAILABLE' || coupon.status === 'IN_USE' ? <Button emphasis="ghost" intent="danger" onClick={() => { setRevokeError(''); setNotice(''); setRevoking(coupon) }} aria-label={`Revocar el cupón ${coupon.code}`}>Revocar</Button> : null}</td>
            <td><strong>{coupon.code}</strong></td>
            <td>{coupon.percentOff} %</td>
            <td>{coupon.periodsUsed.toLocaleString('es-MX')} de {coupon.periods.toLocaleString('es-MX')}</td>
            <td>{formatDay(coupon.lastDay)}</td>
            <td className="admin-courtesy-note">{coupon.note}</td>
            <td>{coupon.createdBy}</td>
            <td><span className="status-chip" data-active={coupon.status === 'AVAILABLE' || coupon.status === 'IN_USE'}>{coupon.status === 'REVOKED' && coupon.revokedBy ? `Revocado por ${coupon.revokedBy}` : statusLabels[coupon.status]}</span></td>
          </tr>)}</tbody>
        </table></div>}
        <div><Button emphasis="outline" onClick={start}>Dar un cupón</Button></div>
      </>}

    <ConfirmDialog open={draft !== null} title="Dar un cupón de descuento" action="Dar cupón y enviarlo" intent="primary" busy={giving} error={giveError || undefined}
      onCancel={() => { if (!giving) setDraft(null) }} onConfirm={() => void give()}>
      {draft && <div className="admin-coupon-form">
        <p>{businessName} recibe por correo un código para canjear en el portal. Desde entonces paga {draft.percentOff} % menos en {draft.periods === 1 ? 'su siguiente pago' : `sus siguientes ${payments(draft.periods)}`}, y lo que agregue en un periodo pagado con el cupón lleva el mismo descuento.</p>
        <div className="admin-coupon-count"><span aria-hidden="true">Descuento, en %</span><QuantityInput label="Descuento, en %" value={draft.percentOff} min={1} max={90} disabled={giving} onChange={(percentOff) => setDraft({ ...draft, percentOff })} /></div>
        <div className="admin-coupon-count"><span aria-hidden="true">Pagos con descuento</span><QuantityInput label="Pagos con descuento" value={draft.periods} min={1} max={24} disabled={giving} onChange={(periods) => setDraft({ ...draft, periods })} /></div>
        <div className="form-field"><label htmlFor={`${id}-last-day`}>Último día para canjearlo</label><input id={`${id}-last-day`} type="date" min={todayIso()} value={draft.lastDay} disabled={giving} onChange={(event) => setDraft({ ...draft, lastDay: event.target.value })} /></div>
        <div className="form-field"><label htmlFor={`${id}-note`}>Motivo</label><textarea id={`${id}-note`} rows={3} maxLength={NOTE_LIMIT} value={draft.note} disabled={giving} aria-describedby={`${id}-note-count`} onChange={(event) => setDraft({ ...draft, note: event.target.value })} /><p id={`${id}-note-count`} className="field-hint">{draft.note.length.toLocaleString('es-MX')} de {NOTE_LIMIT.toLocaleString('es-MX')} caracteres. Solo lo ve el administrador.</p></div>
      </div>}
    </ConfirmDialog>

    <ConfirmDialog open={revoking !== null} title="Revocar el cupón" action="Revocar cupón" busy={revokingBusy} error={revokeError || undefined}
      onCancel={() => { if (!revokingBusy) setRevoking(null) }} onConfirm={() => void revoke()}>
      {revoking && <p>Ningún pago nuevo de {businessName} tomará el cupón {revoking.code}. Si ya lo usa una renovación automática, Stripe conserva el descuento que le queda.</p>}
    </ConfirmDialog>
  </section>
}
